import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  alpha,
  Chip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  MonetizationOn as RevenueIcon,
  People as PatientsIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  SelfImprovement as DoctorIcon,
  ErrorOutline as ErrorIcon,
  TrendingUp,
  TrendingDown,
  ArrowForward,
  Notifications,
  LocalHospital,
  Assessment,
  MoreVert,
  VideoCall,
  Chat,
  MedicalServices,
  Event,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { safeFormatDate } from '../../utils/dateUtils';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { styled } from '@mui/material/styles';
import { 
  fetchDashboardStats, 
  fetchAppointmentHistory, 
  fetchHealthSummary,
  fetchRecentAppointments,
  fetchUpcomingAppointments,
  fetchPrescriptions,
  fetchHealthMetrics
} from '../../features/patients/dashboardSlice';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(10px)',
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: alpha(theme.palette.background.paper, 0.8),
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const PatientAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.primary.main,
  fontSize: '1.2rem',
  fontWeight: 600,
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const StatCard = ({ title, value, icon, color, loading, trend, trendValue }) => (
  <StyledCard>
    <CardContent sx={{ p: 3 }}>
      {loading ? (
        <>
          <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                color: color,
                width: 48,
                height: 48,
                boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
              }}
            >
              {icon}
            </Avatar>
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {trend === 'up' ? (
                  <TrendingUp sx={{ color: 'success.main' }} />
                ) : (
                  <TrendingDown sx={{ color: 'error.main' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: trend === 'up' ? 'success.main' : 'error.main',
                    fontWeight: 500,
                  }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="h6" gutterBottom color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {value}
          </Typography>
        </>
      )}
    </CardContent>
  </StyledCard>
);

const AppointmentCard = ({ appointment, loading }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={60} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateX(4px)',
          boxShadow: theme.shadows[2],
        },
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{appointment.doctor_name}</Typography>
          <Chip
            label={appointment.type}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          />
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarIcon color="action" />
          <Typography variant="body2">
            {safeFormatDate(appointment.date, 'MMMM d, yyyy')}
          </Typography>
          <TimeIcon color="action" sx={{ ml: 2 }} />
          <Typography variant="body2">{appointment.time}</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

const HealthSummaryCard = ({ data, loading }) => {
  const theme = useTheme();
  const COLORS = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.error.main];

  if (loading) {
    return (
      <StyledCard>
        <CardContent>
          <Stack spacing={3}>
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="rectangular" height={200} />
          </Stack>
        </CardContent>
      </StyledCard>
    );
  }

  // Default data if none is provided
  const defaultData = [
    { name: 'No Data', value: 100 }
  ];

  const chartData = data || defaultData;

  return (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Health Summary
        </Typography>
        <Box height={250}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <ChartTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Stack direction="row" spacing={2} justifyContent="center">
          {chartData.map((item, index) => (
            <Stack key={item.name} direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: COLORS[index % COLORS.length],
                }}
              />
              <Typography variant="body2">{item.name}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

const PatientOverview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  // Get dashboard data from Redux store
  const { 
    stats: dashboardStats, 
    appointmentHistory: appointmentHistory,
    healthSummary: healthSummary,
    recentAppointments: recentAppointments,
    upcomingAppointments: upcomingAppointments,
    prescriptions: prescriptions,
    healthMetrics: healthMetrics
  } = useSelector((state) => state.patientDashboard);
  
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch dashboard data
        await Promise.all([
          dispatch(fetchDashboardStats()).unwrap(),
          dispatch(fetchAppointmentHistory(6)).unwrap(),
          dispatch(fetchHealthSummary()).unwrap(),
          dispatch(fetchRecentAppointments()).unwrap(),
          dispatch(fetchUpcomingAppointments()).unwrap(),
          dispatch(fetchPrescriptions()).unwrap(),
          dispatch(fetchHealthMetrics()).unwrap()
        ]);
      } catch (error) {
        setErrorMessage('Unable to load dashboard data. Please try again later.');
      }
    };
    fetchData();
  }, [dispatch]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Error Snackbar */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage('')} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Welcome Section */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                <Avatar
                  src={user?.profile_image}
                  alt={user?.name}
                  sx={{
                    width: 80,
                    height: 80,
                    border: `4px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '2rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.name?.charAt(0) || 'P'}
                </Avatar>
                <Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '1.75rem', sm: '2.25rem' },
                      color: theme.palette.primary.dark,
                      letterSpacing: '-0.5px',
                      mb: 0.5,
                    }}
                  >
                    {getGreeting()}, {user?.name}!
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    Welcome to your health dashboard
                  </Typography>
                </Box>
              </Box>
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2}
              >
                <ActionButton
                  variant="contained"
                  startIcon={<Event />}
                  onClick={() => navigate('/book-appointment')}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Book Appointment
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  startIcon={<Chat />}
                  sx={{
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Message Doctor
                </ActionButton>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    animation: 'pulse 3s infinite',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)', opacity: 0.5 },
                      '50%': { transform: 'scale(1.1)', opacity: 0.3 },
                      '100%': { transform: 'scale(1)', opacity: 0.5 },
                    },
                  }}
                />
                <DoctorIcon sx={{ 
                  fontSize: 200, 
                  opacity: 0.1,
                  color: theme.palette.primary.main,
                  animation: 'float 4s ease-in-out infinite',
                  '@keyframes float': {
                    '0%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-15px)' },
                    '100%': { transform: 'translateY(0px)' },
                  },
                }} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Upcoming Appointments"
            value={dashboardStats.data?.upcoming || 0}
            icon={<CalendarIcon />}
            color={theme.palette.primary.main}
            loading={dashboardStats.loading}
            trend={dashboardStats.data?.upcoming_trend > 0 ? 'up' : 'down'}
            trendValue={dashboardStats.data?.upcoming_trend_text || ''}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Prescriptions"
            value={dashboardStats.data?.active_prescriptions || 0}
            icon={<LocalHospital />}
            color={theme.palette.secondary.main}
            loading={dashboardStats.loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent Consultations"
            value={dashboardStats.data?.recent_consultations || 0}
            icon={<Assessment />}
            color={theme.palette.info.main}
            loading={dashboardStats.loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Expenses"
            value={`$${dashboardStats.data?.total_expenses || 0}`}
            icon={<RevenueIcon />}
            color={theme.palette.success.main}
            loading={dashboardStats.loading}
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Appointment History */}
        <Grid item xs={12} md={8}>
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Appointment History</Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Box>
              {appointmentHistory.loading ? (
                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : appointmentHistory.error ? (
                <Alert severity="error">{appointmentHistory.error}</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={appointmentHistory.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                    <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <ChartTooltip />
                    <Legend />
                    <Bar
                      dataKey="appointments"
                      name="Appointments"
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Health Summary */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <HealthSummaryCard 
              data={healthSummary.data} 
              loading={healthSummary.loading} 
            />
            
            {/* Quick Actions */}
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Assessment />}
                    component={Link}
                    to="/patient-dashboard/medical-records"
                  >
                    View Medical Records
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LocalHospital />}
                    component={Link}
                    to="/patient-dashboard/prescriptions"
                  >
                    View Prescriptions
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RevenueIcon />}
                    component={Link}
                    to="/patient-dashboard/billing"
                  >
                    View Billing
                  </Button>
                </Stack>
              </CardContent>
            </StyledCard>
          </Stack>
        </Grid>

        {/* Upcoming Appointments */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Upcoming Appointments</Typography>
                <Button 
                  variant="text" 
                  endIcon={<ArrowForward />}
                  component={Link}
                  to="/patient-dashboard/appointments"
                >
                  View All
                </Button>
              </Box>
              {upcomingAppointments.loading ? (
                <Box sx={{ py: 2 }}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={60} />
                </Box>
              ) : upcomingAppointments.error ? (
                <Alert severity="error">{upcomingAppointments.error}</Alert>
              ) : upcomingAppointments.data && upcomingAppointments.data.length > 0 ? (
                <Stack spacing={2}>
                  {upcomingAppointments.data.map((appointment) => (
                    <AppointmentCard 
                      key={appointment.id} 
                      appointment={appointment} 
                      loading={false} 
                    />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No upcoming appointments
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/book-appointment')}
                  >
                    Book an Appointment
                  </Button>
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Active Prescriptions */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Active Prescriptions</Typography>
                <Button 
                  variant="text" 
                  endIcon={<ArrowForward />}
                  component={Link}
                  to="/patient-dashboard/prescriptions"
                >
                  View All
                </Button>
              </Box>
              {prescriptions.loading ? (
                <Box sx={{ py: 2 }}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={60} />
                </Box>
              ) : prescriptions.error ? (
                <Alert severity="error">{prescriptions.error}</Alert>
              ) : prescriptions.data && prescriptions.data.length > 0 ? (
                <List>
                  {prescriptions.data.map((prescription) => (
                    <ListItem 
                      key={prescription.id}
                      sx={{ 
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        py: 2
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                          <MedicalServices />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={prescription.medication}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              {prescription.dosage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {format(new Date(prescription.start_date), 'MMM d, yyyy')} - {format(new Date(prescription.end_date), 'MMM d, yyyy')}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        label={prescription.status} 
                        size="small" 
                        color={prescription.status === 'Active' ? 'success' : 'default'}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No active prescriptions
                  </Typography>
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Health Metrics */}
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Health Metrics</Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Box>
              {healthMetrics.loading ? (
                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : healthMetrics.error ? (
                <Alert severity="error">{healthMetrics.error}</Alert>
              ) : healthMetrics.data ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>Mood Trend</Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={healthMetrics.data.mood_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                        <YAxis stroke={theme.palette.text.secondary} />
                        <ChartTooltip />
                        <Line type="monotone" dataKey="value" stroke={theme.palette.primary.main} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>Sleep Hours</Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={healthMetrics.data.sleep_hours}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                        <YAxis stroke={theme.palette.text.secondary} />
                        <ChartTooltip />
                        <Line type="monotone" dataKey="value" stroke={theme.palette.info.main} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>Stress Level</Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={healthMetrics.data.stress_level}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                        <YAxis stroke={theme.palette.text.secondary} />
                        <ChartTooltip />
                        <Line type="monotone" dataKey="value" stroke={theme.palette.error.main} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No health metrics data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientOverview;
